import React, { useState, useEffect } from 'react'
import { Text, Heading, Button } from '../components/ui'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermissions } from '../hooks/usePermissions'
import type { Module } from '../hooks/usePermissions'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../contexts/AuthContext'
import { 
  DocumentTextIcon,
  BellIcon,
  QuestionMarkCircleIcon,
  EyeIcon,
  XMarkIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../constants'
import { supabase } from '../lib/supabase'
import { getVersions } from '../services/informativo'
import { InformativoVersion } from '../types/informativo'
import { modulesService } from '../services/modules'
import { notificationsService, NotificationRecipientItem } from '../services/notifications'
import HelpModal from '../components/HelpModal'
import PdfModal from '../components/PdfModal'
import PWAInstallButton from '../components/PWAInstallButton'
import { getSignedAvatarUrl } from '../services/avatar'
import NotificationDetailModal from '../components/notifications/NotificationDetailModal'

const Dashboard: React.FC = () => {
  const { accessibleModules, loading, error } = usePermissions()
  const { profile } = useAuthContext()
  const navigate = useNavigate()

  // Extrair primeiro nome do usuário
  const getFirstName = () => {
    if (!profile?.name) return ''
    const firstName = profile.name.split(' ')[0]
    return firstName
  }

  // Buscar o título do módulo dashboard
  const [dashboardTitle, setDashboardTitle] = useState<string>('Dashboard')

  useEffect(() => {
    const loadDashboardTitle = async () => {
      // Primeiro, tentar buscar em accessibleModules
      const dashboardModule = accessibleModules.find(
        (module: Module) => module.name === 'dashboard' || module.route === '/dashboard'
      )
      
      if (dashboardModule?.display_name) {
        setDashboardTitle(dashboardModule.display_name)
        return
      }

      // Se não encontrou, buscar diretamente do banco
      try {
        const result = await modulesService.getByName('dashboard')
        if (result.success && result.data?.display_name) {
          setDashboardTitle(result.data.display_name)
        }
      } catch (error) {
        console.error('Erro ao buscar título do módulo dashboard:', error)
      }
    }

    if (!loading) {
      loadDashboardTitle()
    }
  }, [accessibleModules, loading])

  // Estados para versões do Circulando
  const [circulandoVersions, setCirculandoVersions] = useState<InformativoVersion[]>([])
  const [circulandoVersionsLoading, setCirculandoVersionsLoading] = useState<boolean>(true)
  const [circulandoVersionsError, setCirculandoVersionsError] = useState<string | null>(null)

  // Estados para versões da Imprensa
  const [imprensaVersions, setImprensaVersions] = useState<InformativoVersion[]>([])
  const [imprensaVersionsLoading, setImprensaVersionsLoading] = useState<boolean>(true)
  const [imprensaVersionsError, setImprensaVersionsError] = useState<string | null>(null)

  // Estados para versões Internas
  const [internoVersions, setInternoVersions] = useState<InformativoVersion[]>([])
  const [internoVersionsLoading, setInternoVersionsLoading] = useState<boolean>(true)
  const [internoVersionsError, setInternoVersionsError] = useState<string | null>(null)

  // Estados para notificações
  const [notifications, setNotifications] = useState<NotificationRecipientItem[]>([])
  const [notificationsLoading, setNotificationsLoading] = useState<boolean>(true)
  const [notificationsError, setNotificationsError] = useState<string | null>(null)
  const [notificationSendersMap, setNotificationSendersMap] = useState<Record<string, { name: string; avatarUrl: string | null }>>({})

  // Estado para modal de ajuda
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false)
  
  // Estado para modal de notificação completa
  const [selectedNotification, setSelectedNotification] = useState<NotificationRecipientItem | null>(null)
  const [senderInfo, setSenderInfo] = useState<{ name: string; avatarUrl: string | null } | null>(null)
  const [loadingAvatar, setLoadingAvatar] = useState<boolean>(false)

  // Estado para modal de PDF
  const [showPdfModal, setShowPdfModal] = useState<boolean>(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfTitle, setPdfTitle] = useState<string>('Visualizar PDF')

  // Estado para modal de Informativo Interno
  const [showInternoModal, setShowInternoModal] = useState<boolean>(false)
  const [selectedInternoVersion, setSelectedInternoVersion] = useState<InformativoVersion | null>(null)

  // Função para gerar inicial do nome
  const getInitials = (text: string): string => {
    if (!text) return 'N'
    const words = text.trim().split(/\s+/)
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase()
    }
    return text.substring(0, 2).toUpperCase()
  }

  // Função para gerar cor do avatar
  const getAvatarColor = (text: string): string => {
    const colors = [
      'bg-purple-500',
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-teal-500',
    ]
    let hash = 0
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
  }

  // Resetar estados quando uma nova notificação é selecionada
  useEffect(() => {
    if (selectedNotification) {
      setSenderInfo(null)
      setLoadingAvatar(true)
    }
  }, [selectedNotification?.id])

  // Buscar informações do usuário que criou a notificação
  useEffect(() => {
    const fetchSenderInfo = async () => {
      if (!selectedNotification?.notification?.created_by) {
        setSenderInfo(null)
        setLoadingAvatar(false)
        return
      }

      setLoadingAvatar(true)
      try {
        // Buscar perfil do usuário
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('name, avatar_url')
          .eq('id', selectedNotification.notification.created_by)
          .single()

        if (error || !profile) {
          setSenderInfo({ name: 'Usuário', avatarUrl: null })
          setLoadingAvatar(false)
          return
        }

        // Buscar URL assinada do avatar se existir
        let avatarUrl: string | null = null
        if (profile.avatar_url) {
          avatarUrl = await getSignedAvatarUrl(profile.avatar_url)
        }

        setSenderInfo({
          name: profile.name || 'Usuário',
          avatarUrl
        })
      } catch (err) {
        console.error('Erro ao buscar informações do remetente:', err)
        setSenderInfo({ name: 'Usuário', avatarUrl: null })
      } finally {
        setLoadingAvatar(false)
      }
    }

    fetchSenderInfo()
  }, [selectedNotification])

  // Carregar versões do Circulando
  useEffect(() => {
    // Aguarda o carregamento das permissões antes de buscar dados
    if (loading) {
      return
    }

    const loadCirculandoVersions = async () => {
      try {
        setCirculandoVersionsLoading(true)
        setCirculandoVersionsError(null)
        const versions = await getVersions('circulando')
        setCirculandoVersions(versions)
      } catch (e: any) {
        setCirculandoVersionsError(e?.message || 'Erro ao carregar versões do Circulando')
        setCirculandoVersions([])
      } finally {
        setCirculandoVersionsLoading(false)
      }
    }
    loadCirculandoVersions()
  }, [loading])

  // Carregar versões da Imprensa
  useEffect(() => {
    // Aguarda o carregamento das permissões antes de buscar dados
    if (loading) {
      return
    }

    const loadImprensaVersions = async () => {
      try {
        setImprensaVersionsLoading(true)
        setImprensaVersionsError(null)
        const versions = await getVersions('imprensa')
        setImprensaVersions(versions)
      } catch (e: any) {
        setImprensaVersionsError(e?.message || 'Erro ao carregar versões da Imprensa')
        setImprensaVersions([])
      } finally {
        setImprensaVersionsLoading(false)
      }
    }
    loadImprensaVersions()
  }, [loading])

  // Carregar versões Internas
  useEffect(() => {
    // Aguarda o carregamento das permissões antes de buscar dados
    if (loading) {
      return
    }

    const loadInternoVersions = async () => {
      try {
        setInternoVersionsLoading(true)
        setInternoVersionsError(null)
        const versions = await getVersions('interno')
        setInternoVersions(versions)
      } catch (e: any) {
        setInternoVersionsError(e?.message || 'Erro ao carregar versões Internas')
        setInternoVersions([])
      } finally {
        setInternoVersionsLoading(false)
      }
    }
    loadInternoVersions()
  }, [loading])

  // Carregar últimas notificações
  useEffect(() => {
    // Aguarda o carregamento das permissões antes de buscar dados
    if (loading) {
      return
    }

    const loadNotifications = async () => {
      try {
        setNotificationsLoading(true)
        setNotificationsError(null)
        const { data: authInfo } = await supabase.auth.getUser()
        const userId = (authInfo as any)?.user?.id
        
        if (!userId) {
          setNotifications([])
          return
        }

        const { data, error } = await notificationsService.fetchLatest(3, userId)
        
        if (error) {
          throw error
        }
        
        setNotifications(data || [])
      } catch (e: any) {
        setNotificationsError(e?.message || 'Erro ao carregar notificações')
        setNotifications([])
      } finally {
        setNotificationsLoading(false)
      }
    }
    loadNotifications()
  }, [loading])

  // Buscar informações dos remetentes das notificações
  useEffect(() => {
    const fetchNotificationSenders = async () => {
      if (notifications.length === 0) {
        setNotificationSendersMap({})
        return
      }

      // Coletar todos os IDs únicos de remetentes
      const senderIds = Array.from(
        new Set(
          notifications
            .map(it => it.notification?.created_by)
            .filter(Boolean) as string[]
        )
      )

      if (senderIds.length === 0) {
        setNotificationSendersMap({})
        return
      }

      try {
        // Buscar perfis dos remetentes
        const { data: profiles, error } = await supabase
          .from('profiles')
          .select('id, name, avatar_url')
          .in('id', senderIds)

        if (error || !profiles) {
          setNotificationSendersMap({})
          return
        }

        // Criar mapa de remetentes com avatares
        const senders: Record<string, { name: string; avatarUrl: string | null }> = {}
        
        for (const profile of profiles) {
          let avatarUrl: string | null = null
          if (profile.avatar_url) {
            avatarUrl = await getSignedAvatarUrl(profile.avatar_url)
          }
          
          senders[profile.id] = {
            name: profile.name || 'Usuário',
            avatarUrl
          }
        }

        // Preencher com "Usuário" para IDs não encontrados
        senderIds.forEach(id => {
          if (!senders[id]) {
            senders[id] = { name: 'Usuário', avatarUrl: null }
          }
        })

        setNotificationSendersMap(senders)
      } catch (err) {
        console.error('Erro ao buscar informações dos remetentes:', err)
        setNotificationSendersMap({})
      }
    }

    fetchNotificationSenders()
  }, [notifications])

  // Função para obter URL pública do arquivo
  const getFileUrl = (filePath: string | null) => {
    if (!filePath) return null
    const { data } = supabase.storage
      .from('circulando')
      .getPublicUrl(filePath)
    return data.publicUrl
  }

  // Formata descrição para exibir HTML (formatação, imagens) ou texto com quebras
  const formatDescription = (description: string): string => {
    if (!description) return ''
    if (description.includes('<')) return description
    return description.replace(/\n/g, '<br />')
  }

  // Função para normalizar URL (adicionar protocolo se necessário)
  const normalizeUrl = (url: string): string => {
    if (!url) return url
    // Se já tem protocolo, retornar como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    // Se não tem protocolo, adicionar https://
    return `https://${url}`
  }

  // Função para abrir arquivo do artigo em modal (PDF) ou nova aba (link)
  const handleVersionClick = (file: string | null, link: string | null, title?: string) => {
    if (link) {
      // Links abrem em nova aba
      const normalizedLink = normalizeUrl(link)
      window.open(normalizedLink, '_blank')
    } else if (file) {
      // PDFs abrem em modal
      const fileUrl = getFileUrl(file)
      if (fileUrl) {
        setPdfUrl(fileUrl)
        setPdfTitle(title || 'Visualizar PDF')
        setShowPdfModal(true)
      }
    }
  }

  // Função para abrir modal de Informativo Interno
  const handleInternoClick = (version: InformativoVersion) => {
    setSelectedInternoVersion(version)
    setShowInternoModal(true)
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="dashboard" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
            <div className="py-6 px-4 sm:px-6 lg:px-8">
              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando permissões...</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                  <p className="text-red-800 dark:text-red-200">Erro ao carregar permissões: {error}</p>
                </div>
              )}

              {!loading && !error && (
                <>
                  <div className="mb-8">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                          {dashboardTitle}
                        </Heading>
                        <Text size="sm" color="muted" className="mt-2">
                          Bem-vindo{getFirstName() ? `, ${getFirstName()}` : ''}! Aqui está o resumo das informações.
                        </Text>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => setShowHelpModal(true)}
                          variant="outline"
                          className="flex items-center gap-2"
                          aria-label="Sugestões ou Precisa de ajuda"
                        >
                          Sugestões ou Precisa de ajuda
                          <QuestionMarkCircleIcon className="h-5 w-5" />
                        </Button>
                        <PWAInstallButton />
                      </div>
                    </div>
                  </div>

                  {/* Banner de Versões Internas */}
                  {internoVersions.length > 0 && (
                    <div className="mb-8 rounded-lg shadow-lg p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-6 w-6 mr-2" style={{ color: '#008b95' }} />
                          <Heading as="h2" size="2xl" style={{ color: '#008b95' }}>
                            Informativos Internos
                          </Heading>
                        </div>
                        <Button
                          onClick={() => navigate('/informativo/interno')}
                          size="sm"
                          style={{ backgroundColor: '#008b95' }}
                          className="hover:opacity-90 text-white dark:hover:opacity-80"
                        >
                          Ver todos
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {internoVersions.map((version, index) => (
                          <div
                            key={version.date}
                            onClick={() => handleInternoClick(version)}
                            className="bg-white dark:bg-gray-700 rounded-lg p-4 cursor-pointer transition-all duration-200 border relative hover:shadow-md dark:hover:shadow-lg group border-gray-200 dark:border-gray-600"
                          >
                            {index === 0 && (
                              <div className="absolute -top-2 -right-2 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full shadow-md uppercase tracking-wide transition-all duration-300 group-hover:scale-110 group-hover:animate-pulse" style={{ backgroundColor: '#008b95' }}>
                                Novo
                              </div>
                            )}
                            <div className="mb-2">
                              <Text size="sm" weight="bold" className="text-gray-900 dark:text-white line-clamp-2 mb-2">
                                {version.title}
                              </Text>
                              <div
                                className="prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 line-clamp-3 [&>*]:my-0 [&>*]:leading-tight [&_img]:max-w-full [&_img]:h-auto"
                                dangerouslySetInnerHTML={{ __html: formatDescription(version.description) }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {internoVersionsLoading && (
                    <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                      <Text size="sm" color="muted">Carregando versões Internas...</Text>
                    </div>
                  )}

                  {internoVersionsError && !internoVersionsLoading && (
                    <div className="mb-8 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                        {internoVersionsError}
                      </Text>
                    </div>
                  )}

                  {/* Banner de Versões do Circulando */}
                  {circulandoVersions.length > 0 && (
                    <div className="mb-8 rounded-lg shadow-lg p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-6 w-6 mr-2" style={{ color: '#008b95' }} />
                          <Heading as="h2" size="2xl" style={{ color: '#008b95' }}>
                            Circulando
                          </Heading>
                        </div>
                        <Button
                          onClick={() => navigate('/informativo/circulando')}
                          size="sm"
                          style={{ backgroundColor: '#008b95' }}
                          className="hover:opacity-90 text-white dark:hover:opacity-80"
                        >
                          Ver todos
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {circulandoVersions.map((version, index) => (
                          <div
                            key={version.date}
                            onClick={() => handleVersionClick(version.file, version.link, version.title)}
                            className={`bg-white dark:bg-gray-700 rounded-lg p-4 cursor-pointer transition-all duration-200 border relative hover:shadow-md dark:hover:shadow-lg group ${
                              index === 0 
                                ? 'shadow-lg border-gray-200 dark:border-gray-600' 
                                : 'border-gray-200 dark:border-gray-600'
                            }`}
                          >
                            {index === 0 && (
                              <div className="absolute -top-2 -right-2 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full shadow-md uppercase tracking-wide transition-all duration-300 group-hover:scale-110 group-hover:animate-pulse" style={{ backgroundColor: '#008b95' }}>
                                Novo
                              </div>
                            )}
                            <div className="mb-2">
                              <Text size="sm" weight="bold" className="text-gray-900 dark:text-white line-clamp-2 mb-2">
                                {version.title}
                              </Text>
                              <Text size="xs" className="text-gray-600 dark:text-gray-300 line-clamp-3">
                                {version.description}
                              </Text>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {circulandoVersionsLoading && (
                    <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                      <Text size="sm" color="muted">Carregando versões do Circulando...</Text>
                    </div>
                  )}

                  {circulandoVersionsError && !circulandoVersionsLoading && (
                    <div className="mb-8 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                        {circulandoVersionsError}
                      </Text>
                    </div>
                  )}

                  {/* Banner de Versões da Imprensa */}
                  {imprensaVersions.length > 0 && (
                    <div className="mb-8 rounded-lg shadow-lg p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-6 w-6 mr-2" style={{ color: '#008b95' }} />
                          <Heading as="h2" size="2xl" style={{ color: '#008b95' }}>
                            Notícias na Imprensa
                          </Heading>
                        </div>
                        <Button
                          onClick={() => navigate('/informativo/imprensa')}
                          size="sm"
                          style={{ backgroundColor: '#008b95' }}
                          className="hover:opacity-90 text-white dark:hover:opacity-80"
                        >
                          Ver todos
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {imprensaVersions.map((version, index) => (
                          <div
                            key={version.date}
                            onClick={() => handleVersionClick(version.file, version.link)}
                            className={`bg-white dark:bg-gray-700 rounded-lg p-4 cursor-pointer transition-all duration-200 border relative hover:shadow-md dark:hover:shadow-lg group ${
                              index === 0 
                                ? 'shadow-lg border-gray-200 dark:border-gray-600' 
                                : 'border-gray-200 dark:border-gray-600'
                            }`}
                          >
                            {index === 0 && (
                              <div className="absolute -top-2 -right-2 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full shadow-md uppercase tracking-wide transition-all duration-300 group-hover:scale-110 group-hover:animate-pulse" style={{ backgroundColor: '#008b95' }}>
                                Novo
                              </div>
                            )}
                            <div className="mb-2">
                              <Text size="sm" weight="bold" className="text-gray-900 dark:text-white line-clamp-2 mb-2">
                                {version.title}
                              </Text>
                              <Text size="xs" className="text-gray-600 dark:text-gray-300 line-clamp-3">
                                {version.description}
                              </Text>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {imprensaVersionsLoading && (
                    <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                      <Text size="sm" color="muted">Carregando versões da Imprensa...</Text>
                    </div>
                  )}

                  {imprensaVersionsError && !imprensaVersionsLoading && (
                    <div className="mb-8 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                        {imprensaVersionsError}
                      </Text>
                    </div>
                  )}

                  {/* Card de Últimas Notificações */}
                  <div className="mb-8 bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center mb-4">
                        <BellIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400" />
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Últimas Notificações
                        </Heading>
                      </div>
                      
                      {notificationsLoading && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 px-2 py-4">
                          Carregando notificações...
                        </div>
                      )}
                      
                      {notificationsError && !notificationsLoading && (
                        <div className="text-sm text-red-600 dark:text-red-400 px-2 py-4">
                          {notificationsError}
                        </div>
                      )}
                      
                      {!notificationsLoading && !notificationsError && (
                        <>
                          {notifications.length === 0 ? (
                            <div className="text-center py-8">
                              <BellIcon className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                              <Text size="sm" color="muted">
                                Nenhuma notificação encontrada
                              </Text>
                            </div>
                          ) : (
                            <div className="flow-root">
                              <ul className="-mb-8">
                                {notifications.map((notification, idx) => {
                                  const senderId = notification.notification?.created_by
                                  const sender = senderId ? notificationSendersMap[senderId] : null
                                  const senderName = sender?.name || 'Usuário'
                                  const senderAvatarUrl = sender?.avatarUrl || null
                                  
                                  return (
                                    <li key={notification.id}>
                                      <div className="relative pb-8">
                                        {idx !== notifications.length - 1 && (
                                          <span
                                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700"
                                            aria-hidden="true"
                                          />
                                        )}
                                        <div className="relative flex space-x-3">
                                          <div>
                                            <span 
                                              className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white dark:ring-gray-800 overflow-hidden ${
                                                senderAvatarUrl 
                                                  ? '' 
                                                  : !notification.read_at 
                                                    ? 'bg-blue-500' 
                                                    : 'bg-gray-400 dark:bg-gray-600'
                                              }`}
                                            >
                                              {senderAvatarUrl ? (
                                                <img 
                                                  src={senderAvatarUrl} 
                                                  alt={senderName}
                                                  className="w-full h-full object-cover"
                                                />
                                              ) : (
                                                <span className="text-white font-semibold text-xs">
                                                  {getInitials(senderName)}
                                                </span>
                                              )}
                                            </span>
                                          </div>
                                        <div className="min-w-0 flex-1 pt-1.5">
                                          <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                              <Text 
                                                size="sm" 
                                                className={`text-left ${
                                                  !notification.read_at 
                                                    ? 'font-semibold text-gray-900 dark:text-white' 
                                                    : 'text-gray-700 dark:text-gray-300'
                                                }`}
                                              >
                                                {notification.notification?.title || 'Sem título'}
                                              </Text>
                                              <div className="mt-1">
                                                <Text 
                                                  size="xs" 
                                                  className="text-gray-500 dark:text-gray-400 line-clamp-2"
                                                >
                                                  {notification.notification?.body || 'Sem conteúdo'}
                                                </Text>
                                              </div>
                                              <button
                                                onClick={() => setSelectedNotification(notification)}
                                                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline transition-colors cursor-pointer"
                                                type="button"
                                              >
                                                <EyeIcon className="h-3.5 w-3.5" />
                                                Ver mensagem completa
                                              </button>
                                            </div>
                                            {!notification.read_at && (
                                              <span className="ml-2 flex-shrink-0">
                                                <span className="h-2 w-2 bg-blue-500 rounded-full inline-block"></span>
                                              </span>
                                            )}
                                          </div>
                                          <div className="mt-2">
                                            <Text size="xs" className="text-gray-400 dark:text-gray-500">
                                              {notification.notification?.created_at 
                                                ? new Date(notification.notification.created_at).toLocaleString('pt-BR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                  })
                                                : 'Data não disponível'}
                                              {senderName && (
                                                <span className="ml-1">
                                                  {' · '}
                                                  Enviado por {senderName}
                                                </span>
                                              )}
                                            </Text>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </li>
                                )})}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                </>
              )}
            </div>
          </PermissionGuard>
      </div>
      <HelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
      />
      
      {/* Modal de PDF */}
      <PdfModal
        isOpen={showPdfModal}
        onClose={() => {
          setShowPdfModal(false)
          setPdfUrl(null)
          setPdfTitle('Visualizar PDF')
        }}
        pdfUrl={pdfUrl}
        title={pdfTitle}
      />
      
      {/* Modal de Informativo Interno */}
      {showInternoModal && selectedInternoVersion && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowInternoModal(false)
            setSelectedInternoVersion(null)
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto focus:outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 bg-white dark:bg-gray-800">
              <div className="flex-1">
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white mb-2">
                  {selectedInternoVersion.title}
                </Heading>
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4" />
                    {new Date(selectedInternoVersion.date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowInternoModal(false)
                  setSelectedInternoVersion(null)
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Fechar modal"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            {/* Conteúdo */}
            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div
                    className="prose dark:prose-invert prose-sm max-w-none text-gray-900 dark:text-white"
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        const d = selectedInternoVersion.description || ''
                        if (!d) return ''
                        if (d.includes('<')) return d
                        return d.replace(/\n/g, '<br />')
                      })()
                    }}
                  />
                </div>
              </div>
              
              {/* Arquivo se existir */}
              {selectedInternoVersion.file && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Arquivo
                  </label>
                  <Button
                    onClick={() => {
                      const fileUrl = getFileUrl(selectedInternoVersion.file)
                      if (fileUrl) {
                        setPdfUrl(fileUrl)
                        setPdfTitle(selectedInternoVersion.title)
                        setShowPdfModal(true)
                        setShowInternoModal(false)
                      }
                    }}
                    className="flex items-center gap-2"
                    style={{ backgroundColor: BRAND_COLOR }}
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                    Visualizar Arquivo
                  </Button>
                </div>
              )}
              
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowInternoModal(false)
                    setSelectedInternoVersion(null)
                  }}
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Notificação Completa - mesmo modal do menu Notificações (sidebar) */}
      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          senderInfo={senderInfo}
          loadingAvatar={loadingAvatar}
          onClose={() => setSelectedNotification(null)}
        />
      )}
    </div>
  )
}

export default Dashboard
